import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../hooks/ThemeContext';
import { useLocation } from 'react-router-dom';

interface Theme {
    backgroundColor: string;
    textColor: string;
    cardColor: string;
    topbarBackgroundColor: string;
    topbarIconColor: string;
    topbarBorderBottomColor: string;
    sidebarBackgroundColor: string;
    sidebarIconColor: string;
    sidebarIconColorSelected: string;
    sidebarTextColor: string;
    sidebarBorderColor: string;
    borderColor: string;
    categoryIndicatorColor: string;
    cartBackgroundColor: string;
    cartCardColor: string;
    cartTextColor: string;
    cartButtonColor: string;
    cartButtonTextColor: string;
    cartSummaryBorder: string;
    payButtonColor: string;
    payButtonTextColor: string;
    loaderColor: string;
    menuButtons?: boolean;
    searchBarColor?: string;
}

const ThemeCustomizer: React.FC = () => {
    const { theme, updateTheme } = useTheme();
    const [customTheme, setCustomTheme] = useState<Theme>(theme);
    const [originalTheme, setOriginalTheme] = useState<Theme>(theme); // Store original theme
    const [previewUrl, setPreviewUrl] = useState('');
    const location = useLocation();
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Update custom theme when context theme changes, and store original on first load
    useEffect(() => {
        setCustomTheme(theme);
        // Only set original theme once when component first mounts
        setOriginalTheme(prevOriginal => prevOriginal || theme);
    }, [theme]);

    // Set up the preview URL
    useEffect(() => {
        const currentOrigin = window.location.origin;
        const queryParams = new URLSearchParams(window.location.search);
        
        // Get restaurant ID from current URL or default
        const restaurantId = queryParams.get('r') || '';
        
        // Create preview URL - could be root path or specific path
        let targetPath = '/';
        if (restaurantId) {
            targetPath = `/?r=${restaurantId}`;
        }
        
        setPreviewUrl(`${currentOrigin}${targetPath}`);
    }, []);

    // Function to update theme in iframe
    const updateIframeTheme = (newTheme: Partial<Theme>) => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
            try {
                // Post message to iframe to update theme
                iframeRef.current.contentWindow.postMessage({
                    type: 'UPDATE_THEME',
                    theme: newTheme
                }, window.location.origin);
            } catch (error) {
                console.warn('Could not update iframe theme:', error);
            }
        }
    };

    const handleColorChange = (property: keyof Theme, value: string | boolean) => {
        const newTheme = { ...customTheme, [property]: value };
        setCustomTheme(newTheme);
        updateTheme({ [property]: value });
        
        // Also update the iframe
        updateIframeTheme({ [property]: value });
    };

    const downloadThemeConfig = () => {
        const config = {
            customTheme: customTheme
        };
        
        const dataStr = JSON.stringify(config, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        
        const exportFileDefaultName = 'theme-config.json';
        
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
    };

    const resetToDefault = () => {
        // Reset to the original theme that was applied when this component first loaded
        setCustomTheme(originalTheme);
        updateTheme(originalTheme);
        updateIframeTheme(originalTheme);
    };

    const refreshPreview = () => {
        if (iframeRef.current) {
            iframeRef.current.src = iframeRef.current.src;
        }
    };

    return (
        <div style={{ display: 'flex', height: '100vh', fontFamily: 'Arial, sans-serif' }}>
            {/* Left Side - Real Application Preview */}
            <div style={{ flex: 1, position: 'relative', borderRight: '1px solid #ccc' }}>
                {/* Preview Controls */}
                {/* <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    zIndex: 1000,
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center'
                }}>
                    <span>🎨 Live Preview</span>
                    <button
                        onClick={refreshPreview}
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid white',
                            color: 'white',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontSize: '10px'
                        }}
                    >
                        🔄 Refresh
                    </button>
                </div> */}

                {/* Application Iframe */}
                {previewUrl && (
                    <iframe
                        ref={iframeRef}
                        src={previewUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            backgroundColor: 'white'
                        }}
                        title="Application Preview"
                        onLoad={() => {
                            // Send initial theme to iframe after load
                            setTimeout(() => {
                                updateIframeTheme(customTheme);
                            }, 1000);
                        }}
                    />
                )}
            </div>

            {/* Right Side - Theme Controls */}
            <div style={{
                width: '400px',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                overflowY: 'auto',
                height: '100vh',
                boxSizing: 'border-box'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <h2 style={{ margin: '0 0 10px 0', color: '#333' }}>Theme Customizer</h2>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                        Customize your theme colors and see changes in the real application
                    </p>
                </div>

                {/* Action Buttons */}
                <div style={{ marginBottom: '30px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                        onClick={downloadThemeConfig}
                        style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        📥 Download JSON
                    </button>
                    <button
                        onClick={resetToDefault}
                        style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '10px 16px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        🔄 Reset to Default
                    </button>
                </div>

                {/* Theme Controls */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* General Colors */}
                    <ThemeSection title="General">
                        <ColorInput 
                            label="Background Color" 
                            value={customTheme.backgroundColor} 
                            onChange={(value) => handleColorChange('backgroundColor', value)}
                        />
                        <ColorInput 
                            label="Text Color" 
                            value={customTheme.textColor} 
                            onChange={(value) => handleColorChange('textColor', value)}
                        />
                        <ColorInput 
                            label="Card Color" 
                            value={customTheme.cardColor} 
                            onChange={(value) => handleColorChange('cardColor', value)}
                        />
                        <ColorInput 
                            label="Border Color" 
                            value={customTheme.borderColor} 
                            onChange={(value) => handleColorChange('borderColor', value)}
                        />

                        <ColorInput 
                            label="Category Indicator" 
                            value={customTheme.categoryIndicatorColor} 
                            onChange={(value) => handleColorChange('categoryIndicatorColor', value)}
                        />
                        <ColorInput 
                            label="Loader Color" 
                            value={customTheme.loaderColor} 
                            onChange={(value) => handleColorChange('loaderColor', value)}
                        />
                        {customTheme.searchBarColor && (
                            <ColorInput 
                                label="Search Bar" 
                                value={customTheme.searchBarColor} 
                                onChange={(value) => handleColorChange('searchBarColor', value)}
                            />
                        )}

                    </ThemeSection>

                    {/* Topbar Colors */}
                    <ThemeSection title="Topbar">
                        <ColorInput 
                            label="Background" 
                            value={customTheme.topbarBackgroundColor} 
                            onChange={(value) => handleColorChange('topbarBackgroundColor', value)}
                        />
                        <ColorInput 
                            label="Icon Color" 
                            value={customTheme.topbarIconColor} 
                            onChange={(value) => handleColorChange('topbarIconColor', value)}
                        />
                        <ColorInput 
                            label="Border Bottom" 
                            value={customTheme.topbarBorderBottomColor} 
                            onChange={(value) => handleColorChange('topbarBorderBottomColor', value)}
                        />
                    </ThemeSection>

                    {/* Sidebar Colors */}
                    <ThemeSection title="Sidebar">
                        <ColorInput 
                            label="Background" 
                            value={customTheme.sidebarBackgroundColor} 
                            onChange={(value) => handleColorChange('sidebarBackgroundColor', value)}
                        />
                        <ColorInput 
                            label="Text Color" 
                            value={customTheme.sidebarTextColor} 
                            onChange={(value) => handleColorChange('sidebarTextColor', value)}
                        />
                        <ColorInput 
                            label="Icon Color" 
                            value={customTheme.sidebarIconColor} 
                            onChange={(value) => handleColorChange('sidebarIconColor', value)}
                        />
                        <ColorInput 
                            label="Selected Icon" 
                            value={customTheme.sidebarIconColorSelected} 
                            onChange={(value) => handleColorChange('sidebarIconColorSelected', value)}
                        />
                        <ColorInput 
                            label="Border Color" 
                            value={customTheme.sidebarBorderColor} 
                            onChange={(value) => handleColorChange('sidebarBorderColor', value)}
                        />
                    </ThemeSection>

                    {/* Cart Colors */}
                    <ThemeSection title="Cart & Checkout">
                        <ColorInput 
                            label="Cart Background" 
                            value={customTheme.cartBackgroundColor} 
                            onChange={(value) => handleColorChange('cartBackgroundColor', value)}
                        />
                        <ColorInput 
                            label="Cart Card Color" 
                            value={customTheme.cartCardColor} 
                            onChange={(value) => handleColorChange('cartCardColor', value)}
                        />
                        <ColorInput 
                            label="Cart Text" 
                            value={customTheme.cartTextColor} 
                            onChange={(value) => handleColorChange('cartTextColor', value)}
                        />
                        <ColorInput 
                            label="Add to Cart Button" 
                            value={customTheme.cartButtonColor} 
                            onChange={(value) => handleColorChange('cartButtonColor', value)}
                        />
                        <ColorInput 
                            label="Add to Cart Button Text" 
                            value={customTheme.cartButtonTextColor} 
                            onChange={(value) => handleColorChange('cartButtonTextColor', value)}
                        />
                        <ColorInput 
                            label="Pay Button / Location Pop-up Button" 
                            value={customTheme.payButtonColor} 
                            onChange={(value) => handleColorChange('payButtonColor', value)}
                        />
                        <ColorInput 
                            label="Pay Button / Location Pop-up Button Text" 
                            value={customTheme.payButtonTextColor} 
                            onChange={(value) => handleColorChange('payButtonTextColor', value)}
                        />
                    </ThemeSection>

                </div>
            </div>
        </div>
    );
};

const ThemeSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {children}
        </div>
    </div>
);

const ColorInput: React.FC<{ 
    label: string; 
    value: string; 
    onChange: (value: string) => void 
}> = ({ label, value, onChange }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={{ fontSize: '14px', color: '#555', fontWeight: '500' }}>
            {label}
        </label>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    width: '40px',
                    height: '32px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer'
                }}
            />
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                }}
            />
        </div>
    </div>
);

export default ThemeCustomizer;